import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Copy, Image as ImageIcon } from "lucide-react";
import { useConfirm, type ConfirmOptions } from "~/components/confirm-dialog";
import { DetailField } from "~/components/detail-field";
import { FieldError } from "~/components/field-error";
import { ParcelMoney } from "~/components/ops/parcel-money";
import { ParcelConversation, ResolveConversation } from "~/components/ops/parcel-conversation";
import { PricingDialog } from "~/components/ops/pricing-dialog";
import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import { Textarea } from "~/components/ui/textarea";
import { api, APIError, type OpsTransition } from "~/lib/api";
import { useCopy } from "~/lib/clipboard";
import { etaIsLate, formatDate, formatDay, formatEta, formatKES, statusLabel, toNum } from "~/lib/format";
import { MODE_LABEL } from "~/lib/modes";
import { countryName, deliveryLabel } from "~/lib/parcel-status";
import type { IParcel } from "~/lib/proto";
import { cn } from "~/lib/utils";

const ACTOR_LABEL: Record<string, string> = { ops: "staff", customer: "the customer", mpesa: "M-Pesa" };

function actorLabel(actor?: string | null): string {
  const label = ACTOR_LABEL[(actor ?? "").split(":")[0]];
  return label ? `, by ${label}` : "";
}

type Action = { label: string; run: () => void };
type NextStep = { context: string; primary?: Action; more?: Action[] };

export function ParcelDetailPanel({ parcelId }: { parcelId: string }) {
  const parcel = useQuery({ queryKey: ["parcel", parcelId], queryFn: () => api.getParcel(parcelId) });

  if (parcel.isLoading) {
    return (
      <div className="space-y-4 p-4" aria-busy>
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }
  if (!parcel.data) {
    return (
      <div className="p-8 text-center">
        <p className="text-[14px] font-medium">This parcel did not load</p>
        <p className="text-text-faint mt-1 text-[13px]">Check your connection, then try again.</p>
        <Button variant="outline" className="mt-3" onClick={() => void parcel.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  return <ParcelPanel key={parcelId} parcel={parcel.data} />;
}

function ParcelPanel({ parcel }: { parcel: IParcel }) {
  const id = parcel.id!;
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [pricing, setPricing] = useState(false);
  const [holding, setHolding] = useState(false);
  const [prompting, setPrompting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = parcel.status ?? "";
  const customer = parcel.customerName || "The customer";
  const title = parcel.description || "this parcel";
  const due = toNum(parcel.amountChargedKes);

  async function act(question: ConfirmOptions, work: () => Promise<unknown>) {
    if (!(await confirm(question))) return;
    setBusy(true);
    setError(null);
    try {
      await work();
      void qc.invalidateQueries({ queryKey: ["ops", "parcels"] });
      void qc.invalidateQueries({ queryKey: ["ops", "transactions"] });
      await qc.invalidateQueries({ queryKey: ["parcel", id] });
    } catch (e) {
      setError(e instanceof APIError ? e.message : "That did not go through. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  const move = (action: OpsTransition, question: ConfirmOptions) => () => void act(question, () => api.ops.transition(id, action));

  const steps: Record<string, NextStep> = {
    REGISTERED: {
      context: "Waiting for the seller to deliver it to the warehouse abroad.",
      primary: {
        label: "Mark received abroad",
        run: move("receive", {
          title: `Mark ${title} as received abroad?`,
          body: `${customer} gets an SMS that it reached the ${countryName(parcel.originCountry)} warehouse. This cannot be undone.`,
          confirmLabel: "Mark received",
        }),
      },
    },
    RECEIVED_ABROAD: {
      context: "Travelling to Kenya. When it lands, set what the customer owes.",
      primary: { label: "Landed in Kenya", run: () => setPricing(true) },
    },
    RECEIVED_KENYA: {
      context: `Waiting for ${customer} to pay ${formatKES(due)}. It moves on by itself once they do.`,
      primary: { label: "Send M-Pesa prompt", run: () => setPrompting(true) },
      more: [
        {
          label: "Mark paid without M-Pesa",
          run: () =>
            void act(
              {
                title: `Record ${formatKES(due)} as paid?`,
                body: `Only when ${customer} has already paid by cash or another way. No M-Pesa prompt is sent, and this cannot be undone.`,
                confirmLabel: "Record payment",
              },
              () => api.ops.markPaid(id),
            ),
        },
        { label: "Put on hold", run: () => setHolding(true) },
      ],
    },
    ON_HOLD: {
      context: "On hold. Add anything extra the customer owes below, then release it so they can pay.",
      primary: {
        label: "Release hold",
        run: move("release", {
          title: "Release the hold?",
          body: `It goes back to awaiting payment with ${formatKES(due)} due, and ${customer} can pay.`,
          confirmLabel: "Release hold",
        }),
      },
    },
    PAID: {
      context: `${customer} has paid. Settle with the freighter to send it out. The customer already sees it as out for delivery.`,
      primary: {
        label: "Mark freighter settled",
        run: move("settle", {
          title: "Mark the freighter as settled?",
          body: "The parcel moves to out for delivery. The customer is not notified, and this cannot be undone.",
          confirmLabel: "Mark settled",
        }),
      },
    },
    OUT_FOR_DELIVERY: {
      context: `Going to ${deliveryLabel(parcel)}.`,
      primary: {
        label: "Mark delivered",
        run: move("deliver", {
          title: `Mark ${title} as delivered?`,
          body: `${customer} gets a delivery SMS and the parcel leaves the active list. This cannot be undone.`,
          confirmLabel: "Mark delivered",
        }),
      },
    },
    DELIVERED: { context: "Delivered. Nothing left to do." },
  };
  const step = steps[status] ?? { context: "" };
  const eta = formatEta(parcel.etaMinAt, parcel.etaMaxAt);
  const late = etaIsLate(parcel.etaMaxAt, status);
  const events = (parcel.events ?? []).slice().sort((a, b) => toNum(a.id) - toNum(b.id));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 md:pr-10">
        <header>
          <h2 className="section-heading truncate">{parcel.customerName || "No name"}</h2>
          <p className="text-text-faint mt-0.5 flex flex-wrap gap-x-3 text-[12px]">
            {parcel.customerPhone && (
              <a href={`tel:${parcel.customerPhone}`} className="text-foreground tabular underline-offset-2 hover:underline">
                {parcel.customerPhone}
              </a>
            )}
            {parcel.customerPpCode && <span>Customer code {parcel.customerPpCode}</span>}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <StatusBadge status={status} />
            <span className="min-w-0 truncate text-[13px] font-medium">{parcel.description || "Parcel"}</span>
          </div>
          {parcel.trackingNumber && <CopyLine label="Tracking" value={parcel.trackingNumber} />}
        </header>

        <NextStepBlock step={step} busy={busy} error={error} className="bg-muted/50 rounded-lg p-3 max-md:hidden" />

        <Section title="Money">
          <ParcelMoney parcel={parcel} />
        </Section>

        <Section title="Delivery">
          <p className="text-[13px]">{deliveryLabel(parcel)}</p>
          {parcel.addressProof && (
            <a
              href={api.addressProofUrl(parcel.addressProof)}
              target="_blank"
              rel="noreferrer"
              className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-medium underline underline-offset-2"
            >
              <ImageIcon className="size-3.5" strokeWidth={1.75} />
              Address photo from the customer
            </a>
          )}
        </Section>

        <Section title="Parcel">
          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <DetailField label="Parcel ref"><span className="tabular">{parcel.labelCode || "Not assigned"}</span></DetailField>
            <DetailField label="Route">
              {countryName(parcel.originCountry)}
              {parcel.mode ? `, ${MODE_LABEL[parcel.mode]?.toLowerCase()}` : ""}
            </DetailField>
            <DetailField label="Freighter">{parcel.freighter || "None on this route"}</DetailField>
            <DetailField label="Seller's date">
              {parcel.expectedArrival ? formatDay(parcel.expectedArrival) : "Not given"}
              {parcel.warehouseNotifiedAt ? ", warehouse asked" : ""}
            </DetailField>
            <DetailField label="Estimated delivery">
              {eta ? <span className={late ? "font-medium" : undefined}>{late ? `Overdue, was ${eta}` : eta}</span> : "No estimate"}
            </DetailField>
            {parcel.category && <DetailField label="Category"><span className="capitalize">{parcel.category}</span></DetailField>}
            {toNum(parcel.declaredValueKes) > 0 && <DetailField label="Declared value">{formatKES(parcel.declaredValueKes)}</DetailField>}
          </div>
        </Section>

        {events.length > 0 && (
          <Section title="History">
            <ol className="space-y-2">
              {events.map((event) => (
                <li key={toNum(event.id)} className="flex items-start gap-2.5">
                  <span className="bg-foreground/40 mt-1.5 size-1.5 shrink-0 rounded-full" />
                  <div className="min-w-0 leading-tight">
                    <p className="text-[13px]">{statusLabel(event.toStatus)}</p>
                    <p className="text-text-faint text-[11px]">
                      {formatDate(event.createdAt)}
                      {actorLabel(event.actor)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}

        <section className="border-hairline border-t pt-4">
          <div className="mb-2 flex min-h-7 items-center justify-between gap-3">
            <h3 className="eyebrow">Messages</h3>
            <ResolveConversation parcelId={id} />
          </div>
          <ParcelConversation parcelId={id} />
        </section>
      </div>

      <NextStepBlock step={step} busy={busy} error={error} className="border-hairline bg-surface border-t p-3 md:hidden" />

      <PricingDialog parcel={parcel} open={pricing} onOpenChange={setPricing} />
      <HoldDialog parcel={parcel} open={holding} onOpenChange={setHolding} />
      <PromptDialog parcel={parcel} open={prompting} onOpenChange={setPrompting} />
    </div>
  );
}

function NextStepBlock({ step, busy, error, className }: { step: NextStep; busy: boolean; error: string | null; className?: string }) {
  return (
    <section className={cn("space-y-2.5", className)}>
      <p className="text-[13px] leading-snug">{step.context}</p>
      {(step.primary || step.more) && (
        <div className="flex flex-wrap gap-2">
          {step.primary && (
            <Button size="sm" className="max-md:h-11 max-md:flex-1 max-md:text-[15px]" loading={busy} onClick={step.primary.run}>
              {step.primary.label}
            </Button>
          )}
          {step.more && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="max-md:h-11 max-md:text-[15px]" disabled={busy}>
                  More <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {step.more.map((action) => (
                  <DropdownMenuItem key={action.label} className="max-md:min-h-11" onSelect={action.run}>
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      <FieldError>{error}</FieldError>
    </section>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-hairline border-t pt-4">
      <h3 className="eyebrow mb-2">{title}</h3>
      {children}
    </section>
  );
}

function CopyLine({ label, value }: { label: string; value: string }) {
  const { copied, copy } = useCopy();
  return (
    <button
      type="button"
      onClick={() => copy(value)}
      title="Copy"
      className="text-text-faint hover:text-foreground mt-1.5 flex max-w-full items-center gap-1.5 text-left text-[12px] transition-colors"
    >
      <span className="shrink-0">{label}</span>
      <span className="text-foreground tabular min-w-0 truncate">{value}</span>
      {copied ? <Check className="size-3.5 shrink-0" /> : <Copy className="size-3.5 shrink-0" />}
      {copied && <span role="status">Copied</span>}
    </button>
  );
}

function useParcelRefresh(parcelId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["ops", "parcels"] });
    void qc.invalidateQueries({ queryKey: ["ops", "transactions"] });
    void qc.invalidateQueries({ queryKey: ["parcel", parcelId] });
    void qc.invalidateQueries({ queryKey: ["conversation", parcelId] });
    void qc.invalidateQueries({ queryKey: ["ops", "conversations"] });
  };
}

function HoldDialog({ parcel, open, onOpenChange }: { parcel: IParcel; open: boolean; onOpenChange: (v: boolean) => void }) {
  const refresh = useParcelRefresh(parcel.id!);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const customer = parcel.customerName || "The customer";

  async function hold() {
    if (!reason.trim()) return setError(`Say what you need from ${customer}. They see this message.`);
    setBusy(true);
    setError(null);
    try {
      await api.ops.hold(parcel.id!, { reason: reason.trim(), amountKes: amount ? Math.round(Number(amount) * 100) : undefined });
      refresh();
      onOpenChange(false);
      setReason("");
      setAmount("");
    } catch (e) {
      setError(e instanceof APIError ? e.message : "The hold was not saved. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Put this parcel on hold</DialogTitle>
          <DialogDescription>
            {customer} cannot pay until you release it. They get your message below, and can reply to it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="hold-reason">What you need from the customer</Label>
            <Textarea id="hold-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="KRA needs a copy of your ID and a clearance fee before we can release this." />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hold-amount">Extra charge in KES, if any</Label>
            <Input id="hold-amount" type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
          </div>
          <FieldError>{error}</FieldError>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button loading={busy} onClick={() => void hold()}>
            Put on hold
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromptDialog({ parcel, open, onOpenChange }: { parcel: IParcel; open: boolean; onOpenChange: (v: boolean) => void }) {
  const refresh = useParcelRefresh(parcel.id!);
  const customerPhone = parcel.customerPhone ?? "";
  const [phone, setPhone] = useState(customerPhone);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(customerPhone);
    setError(null);
  }, [open, customerPhone]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const other = phone.trim() && phone.trim() !== customerPhone ? phone.trim() : undefined;
      await api.ops.promptPayment(parcel.id!, other);
      refresh();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof APIError ? e.message : "The prompt was not sent. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send an M-Pesa prompt for {formatKES(parcel.amountChargedKes)}</DialogTitle>
          <DialogDescription>
            The phone below gets a prompt to enter its M-Pesa PIN. Use another number if someone else is paying.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="prompt-number">M-Pesa number</Label>
          <Input id="prompt-number" type="tel" inputMode="tel" value={phone} placeholder={customerPhone} onChange={(e) => setPhone(e.target.value)} />
          <FieldError>{error}</FieldError>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button loading={busy} onClick={() => void send()}>
            Send prompt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
